import { useEffect, useMemo, useRef } from 'react';
import { cellToLatLng, polygonToCells } from 'h3-js';
import { useQuery } from 'react-query';
import { useFilterToolbarState } from 'hooks/filterToolbar/useFilterToolbarState';
import { useAreMapFiltersReady } from 'hooks/projectMap/useAreMapFiltersReady';
import { useScoutbotSavedSearchQueries } from 'hooks/projectMap/useScoutbotSavedSearchQueries';
import { useDebounce } from 'hooks/useDebounced';
import { fetchMapCluster } from 'pages/ProjectInsights/services';
import {
  useClusteringH4,
  useClusteringH5,
  useClusteringH6,
  useClusteringH7,
  useClusteringH10,
  useClusteringH8,
  useClusteringH9,
  useClearAllState,
  useClustersToDisplay,
} from 'state/filterMapPins';
import { useSelectedPolygonBoundsOnMap } from 'state/map';
import { useMapBoundsAsCardinalDirections } from 'state/mapSettings';
import { useZoomQuery } from 'state/queryParams';
import { ZOOM_THRESHOLD } from 'utils/constants';
import { formatBoundsAsGeoJSON } from 'utils/format';

const CUT_OFF_ZOOM_LVL = ZOOM_THRESHOLD;

const CLUSTER_LVL = {
  H4: 4,
  H5: 5,
  H6: 6,
  H7: 7,
  H8: 8,
  H9: 9,
  H10: 10,
};

const getClusteringName = (resolution: number) => {
  switch (resolution) {
    case CLUSTER_LVL.H10:
      return 'clustering_h10';
    case CLUSTER_LVL.H9:
      return 'clustering_h9';
    case CLUSTER_LVL.H8:
      return 'clustering_h8';
    case CLUSTER_LVL.H7:
      return 'clustering_h7';
    case CLUSTER_LVL.H6:
      return 'clustering_h6';
    case CLUSTER_LVL.H5:
      return 'clustering_h5';
    default:
      return 'clustering_h4';
  }
};

/**
 * Fetches the map's H3 cluster counts for the current viewport, filters and
 * (optionally) selected saved search.
 *
 * Fetching is fully declarative: a single react-query drives it, keyed by the
 * viewport, resolution and a stable "filter identity", with both the key and
 * the `enabled` gate read from one debounced snapshot so they never disagree.
 * It only runs once useAreMapFiltersReady is true - i.e. the default region
 * has resolved, or the selected saved search has loaded - which is what stops
 * the old fetch-with-empty-region-then-refetch double request. When the
 * filter identity changes (filters edited, saved search switched, polygon
 * drawn) the cached cells are cleared and the whole viewport is refetched, so
 * counts always reflect the active filters rather than merging across them.
 */
export const useMapClusters = () => {
  const [mapBoundsAsCardinalDirections] = useMapBoundsAsCardinalDirections();
  const [zoomQuery] = useZoomQuery();
  const [, setClustersForCurrentResolution] = useClustersToDisplay();

  const { clusteringH4, updateClusteringH4 } = useClusteringH4();
  const { clusteringH5, updateClusteringH5 } = useClusteringH5();
  const { clusteringH6, updateClusteringH6 } = useClusteringH6();
  const { clusteringH7, updateClusteringH7 } = useClusteringH7();
  const { clusteringH8, updateClusteringH8 } = useClusteringH8();
  const { clusteringH9, updateClusteringH9 } = useClusteringH9();
  const { clusteringH10, updateClusteringH10 } = useClusteringH10();
  const { clearAllMapPinsAndClusters } = useClearAllState();
  const { getFiltersAsQueryParams, getMapStateAsQueryParams } = useFilterToolbarState();
  const [selectedPolygonBoundsOnMap] = useSelectedPolygonBoundsOnMap();
  const { isScoutSearch, rawQueries } = useScoutbotSavedSearchQueries();
  const areMapFiltersReady = useAreMapFiltersReady();

  const resolution = useMemo(() => {
    if (Number(zoomQuery) >= CUT_OFF_ZOOM_LVL) {
      return 0;
    }
    if (Number(zoomQuery) >= 13) {
      return CLUSTER_LVL.H8;
    }
    if (Number(zoomQuery) >= 12) {
      return CLUSTER_LVL.H7;
    }
    if (Number(zoomQuery) >= 11) {
      return CLUSTER_LVL.H6;
    }
    if (Number(zoomQuery) >= 9) {
      return CLUSTER_LVL.H5;
    }

    return CLUSTER_LVL.H4;
  }, [zoomQuery]);

  // Everything about the request that defines which projects match, minus the
  // viewport/resolution. When it changes the cached cells no longer apply and
  // the viewport is refetched; panning/zooming keeps the same identity and so
  // reuses the cells. Derived from the query params actually sent, so it
  // changes exactly when the resulting request would differ (e.g. once the
  // default region resolves).
  const filterIdentity = useMemo(
    () => {
      const { search, radius } = getMapStateAsQueryParams();

      return JSON.stringify({
        filters: getFiltersAsQueryParams(),
        isScoutSearch,
        rawQueries,
        polygon: selectedPolygonBoundsOnMap,
        search,
        radius,
      });
    },
    // getFiltersAsQueryParams / getMapStateAsQueryParams are fresh closures each
    // render; their output is what matters and is covered by the state deps they
    // read (getFiltersAsQueryParams covers the filters; getMapStateAsQueryParams
    // covers search/radius, which also drive the cluster request).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getFiltersAsQueryParams,
      getMapStateAsQueryParams,
      isScoutSearch,
      rawQueries,
      selectedPolygonBoundsOnMap,
    ],
  );

  // Debounce every input the query reads together, so the key and the
  // `enabled` gate always reflect the same snapshot (no skew window where an
  // out-of-date fetch could slip through).
  const debounced = useDebounce(
    {
      mapBoundsAsCardinalDirections,
      resolution,
      filterIdentity,
      zoomQuery,
      isScoutSearch,
      rawQueries,
      areMapFiltersReady,
    },
    400, // 400 is minimum to prevent zoom and bound conflict
  );

  // Which filter identity each resolution's cached cell set was fetched
  // under. Only cells written under the identity still current are ever
  // merged into; anything else is fully replaced, so counts can never blend
  // two filter sets.
  const cacheIdentityRef = useRef<Record<number, string>>({});

  // Bumped whenever the filter identity changes. A fetch started under an old
  // identity is stale once this moves (nothing cancels fetchMapCluster), so
  // queryFn captures the generation up front and drops its result instead of
  // writing it if the identity changed while the request was in flight.
  const fetchGenerationRef = useRef(0);

  const getClustersForResolution = (targetResolution: number) => {
    switch (targetResolution) {
      case CLUSTER_LVL.H10:
        return clusteringH10;
      case CLUSTER_LVL.H9:
        return clusteringH9;
      case CLUSTER_LVL.H8:
        return clusteringH8;
      case CLUSTER_LVL.H7:
        return clusteringH7;
      case CLUSTER_LVL.H6:
        return clusteringH6;
      case CLUSTER_LVL.H5:
        return clusteringH5;
      default:
        return clusteringH4;
    }
  };

  const setClusteringForResolution = (
    data: Record<string, { index: string; count: number; centroid?: number[] }>,
    targetResolution: number,
  ) => {
    // Replace (not merge) the resolution's cells with exactly what the current
    // viewport + filters returned, so counts can never blend two filter sets.
    switch (targetResolution) {
      case CLUSTER_LVL.H10:
        updateClusteringH10(data);
        break;
      case CLUSTER_LVL.H9:
        updateClusteringH9(data);
        break;
      case CLUSTER_LVL.H8:
        updateClusteringH8(data);
        break;
      case CLUSTER_LVL.H7:
        updateClusteringH7(data);
        break;
      case CLUSTER_LVL.H6:
        updateClusteringH6(data);
        break;
      case CLUSTER_LVL.H5:
        updateClusteringH5(data);
        break;
      default:
        updateClusteringH4(data);
    }
  };

  useQuery({
    queryKey: [
      'fetch-map-clusters',
      debounced.filterIdentity,
      debounced.resolution,
      debounced.mapBoundsAsCardinalDirections,
    ],
    queryFn: async () => {
      if (!debounced.mapBoundsAsCardinalDirections) {
        return [];
      }
      const mapBoundsPolygon = formatBoundsAsGeoJSON(
        debounced.mapBoundsAsCardinalDirections,
      );
      const cellsInViewport = polygonToCells(mapBoundsPolygon, debounced.resolution);
      const generation = fetchGenerationRef.current;

      const allFilters = getFiltersAsQueryParams();
      const mapState = getMapStateAsQueryParams();

      if (selectedPolygonBoundsOnMap) {
        mapState.pathOrBounds = selectedPolygonBoundsOnMap;
      }

      // A scoutbot search's real filters live in raw_queries - the individual
      // filter fields stay empty, so only the date/status fields (which the
      // raw-queries path also applies) still apply on top of it.
      const filterState = debounced.isScoutSearch
        ? {
            raw_queries: debounced.rawQueries,
            startDate: allFilters.startDate,
            createdAtStart: allFilters.createdAtStart,
            createdAtEnd: allFilters.createdAtEnd,
            status: allFilters.status,
          }
        : allFilters;

      // Reuse cached counts when the cache provably belongs to the current
      // filter identity: request only the viewport's not-yet-cached cells and
      // merge, so ordinary panning/zooming doesn't re-query cells we already
      // have (and costs nothing when the viewport is fully cached). On a
      // filter-identity change the cached set is never trusted - the whole
      // viewport is requested and the bucket replaced.
      const sameIdentity =
        cacheIdentityRef.current[debounced.resolution] === debounced.filterIdentity;
      const cached = sameIdentity ? getClustersForResolution(debounced.resolution) : {};
      const cellsWeNeed = cellsInViewport.filter((cell) => !cached[cell]);

      if (sameIdentity && cellsWeNeed.length === 0) {
        // Fully cached under the current filters - nothing to fetch.
        return [];
      }

      const cellsWithCounts = await fetchMapCluster({
        ...filterState,
        ...mapState,
        cellsWeNeed,
        clusterResolution: getClusteringName(debounced.resolution),
      });

      // The filter identity changed while this request was in flight: the
      // cache was cleared for the new identity, so dropping the stale result
      // here is what keeps old-filter cells from being spliced back in.
      if (fetchGenerationRef.current !== generation) {
        return [];
      }

      const normalized = cellsWithCounts.reduce(
        (acc, cell) => {
          acc[cell.value] = {
            index: cell.value,
            count: cell.count,
            centroid: cellToLatLng(cell.value),
          };

          return acc;
        },
        {} as Record<string, { index: string; count: number; centroid: number[] }>,
      );

      cacheIdentityRef.current[debounced.resolution] = debounced.filterIdentity;
      setClusteringForResolution({ ...cached, ...normalized }, debounced.resolution);

      return cellsWithCounts;
    },
    refetchOnWindowFocus: false,
    enabled:
      !!debounced.mapBoundsAsCardinalDirections &&
      Number(debounced.zoomQuery) < CUT_OFF_ZOOM_LVL &&
      (!debounced.isScoutSearch || !!debounced.rawQueries) &&
      debounced.areMapFiltersReady,
    cacheTime: 0,
    staleTime: Infinity,
  });

  // Clear cached cells when the filter identity changes (filters edited, saved
  // search switched, polygon drawn) - kept in step with the query by keying
  // off the same debounced identity, so the old view holds until the refetch
  // replaces it rather than flashing empty. Panning/zooming keeps the same
  // identity and is untouched.
  useEffect(() => {
    fetchGenerationRef.current += 1;
    clearAllMapPinsAndClusters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced.filterIdentity]);

  useEffect(() => {
    let clusters = {};
    switch (resolution) {
      case CLUSTER_LVL.H10:
        clusters = clusteringH10;
        break;
      case CLUSTER_LVL.H9:
        clusters = clusteringH9;
        break;
      case CLUSTER_LVL.H8:
        clusters = clusteringH8;
        break;
      case CLUSTER_LVL.H7:
        clusters = clusteringH7;
        break;
      case CLUSTER_LVL.H6:
        clusters = clusteringH6;
        break;
      case CLUSTER_LVL.H5:
        clusters = clusteringH5;
        break;
      case CLUSTER_LVL.H4:
        clusters = clusteringH4;
        break;
      default:
        clusters = {};
    }
    setClustersForCurrentResolution(clusters);
  }, [
    resolution,
    clusteringH4,
    clusteringH5,
    clusteringH6,
    clusteringH7,
    clusteringH8,
    clusteringH9,
    clusteringH10,
    setClustersForCurrentResolution,
  ]);
};
